import React, { useEffect, useMemo, useState } from 'react'
import PlayerSettlementViewModal from '../components/PlayerSettlementViewModal'
import { apiGet } from '../utils/api'

// Функция для форматирования чисел с разделителями тысяч в русской локали (округление до сотых)
const formatNumber = (num: number): string => {
  return num.toLocaleString('ru-RU', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

type Row = {
  playerId: string
  playerName: string
  opening: number
  incoming: number // начислено (RECEIVABLE)
  outgoing: number // оплаты (PAYMENT)
  closing: number
  tournaments?: Array<{
    tournamentId: string | null
    tournamentName: string
    tournamentEndDate: string | null
    opening: number
    incoming: number
    outgoing: number
    closing: number
  }>
}

type Detail = {
  id?: string
  date: string
  document?: string
  type: string
  amount: number
  tournament?: string
  level?: string
}

type TournamentOption = {
  id: string
  name: string
  status?: string
  startDate?: string
  endDate?: string
}


const ReportsPlayerSettlements: React.FC = () => {
  const [rows, setRows] = useState<Row[]>([])
  const [loading, setLoading] = useState(false)
  const [from, setFrom] = useState<string>('')
  const [to, setTo] = useState<string>('')
  const [tournamentId, setTournamentId] = useState<string>('')
  const [playerFilter, setPlayerFilter] = useState<string>('')
  const [groupByTournament, setGroupByTournament] = useState<boolean>(false)
  const [detailFor, setDetailFor] = useState<{
    id: string
    name: string
    opening: number
    tournamentName?: string
  } | null>(null)
  const [details, setDetails] = useState<Detail[]>([])
  const [dLoading, setDLoading] = useState(false)
  const [tournaments, setTournaments] = useState<TournamentOption[]>([])
  const [viewOpen, setViewOpen] = useState(false)
  const [viewData, setViewData] = useState<any>(null)

  // Загрузка сохраненного фильтра турнира и периода из localStorage
  useEffect(() => {
    const savedTournamentId = localStorage.getItem('reports-player-settlements-tournament-id')
    const savedFrom = localStorage.getItem('reports-player-settlements-from')
    const savedTo = localStorage.getItem('reports-player-settlements-to')
    
    if (savedTournamentId !== null) {
      setTournamentId(savedTournamentId)
    }
    if (savedFrom) {
      setFrom(savedFrom)
    }
    if (savedTo) {
      setTo(savedTo)
    }
  }, [])

  // Сохранение фильтра турнира в localStorage при изменении
  const handleTournamentChange = (value: string) => {
    setTournamentId(value)
    // Сохраняем любое значение, включая пустое ("Все")
    localStorage.setItem('reports-player-settlements-tournament-id', value)
  }

  // Сохранение периода в localStorage при изменении
  const handleFromChange = (value: string) => {
    setFrom(value)
    localStorage.setItem('reports-player-settlements-from', value)
  }

  const handleToChange = (value: string) => {
    setTo(value)
    localStorage.setItem('reports-player-settlements-to', value)
  }

  const total = useMemo(() => {
    const normalizedFilter = playerFilter.trim().toLowerCase()
    const filteredRows = normalizedFilter
      ? rows.filter((row) => String(row.playerName || '').toLowerCase().includes(normalizedFilter))
      : rows

    return filteredRows.reduce(
      (a, r) => ({
        opening: a.opening + r.opening,
        incoming: a.incoming + r.incoming,
        outgoing: a.outgoing + r.outgoing,
        closing: a.closing + r.closing,
      }),
      { opening: 0, incoming: 0, outgoing: 0, closing: 0 }
    )
  }, [rows, playerFilter])

  const filteredRows = useMemo(() => {
    const normalizedFilter = playerFilter.trim().toLowerCase()
    if (!normalizedFilter) return rows
    return rows.filter((row) => String(row.playerName || '').toLowerCase().includes(normalizedFilter))
  }, [rows, playerFilter])

  const tournamentStatusMeta: Record<string, { order: number; label: string }> = {
    ACTIVE: { order: 0, label: 'Активные' },
    PLANNED: { order: 1, label: 'Запланированные' },
    COMPLETED: { order: 2, label: 'Завершенные' },
    CANCELLED: { order: 3, label: 'Отмененные' }
  }

  const tournamentsGrouped = useMemo(() => {
    const toDateTs = (value?: string): number => {
      if (!value) return Number.NEGATIVE_INFINITY
      const parsed = new Date(value).getTime()
      return Number.isFinite(parsed) ? parsed : Number.NEGATIVE_INFINITY
    }

    const groups = new Map<string, { label: string; order: number; items: TournamentOption[] }>()
    for (const tournament of tournaments) {
      const statusKey = String(tournament.status || '').trim().toUpperCase()
      const statusInfo = tournamentStatusMeta[statusKey] || { order: 99, label: 'Прочие' }
      if (!groups.has(statusInfo.label)) {
        groups.set(statusInfo.label, { label: statusInfo.label, order: statusInfo.order, items: [] })
      }
      groups.get(statusInfo.label)!.items.push(tournament)
    }

    const sortedGroups = Array.from(groups.values()).sort((a, b) => a.order - b.order || a.label.localeCompare(b.label, 'ru'))
    for (const group of sortedGroups) {
      group.items.sort((a, b) => {
        const aTs = Math.max(toDateTs(a.endDate), toDateTs(a.startDate))
        const bTs = Math.max(toDateTs(b.endDate), toDateTs(b.startDate))
        if (aTs !== bTs) return bTs - aTs
        return String(a.name || '').localeCompare(String(b.name || ''), 'ru')
      })
    }
    return sortedGroups
  }, [tournaments])

  const fetchTournaments = async () => {
    try {
      const data = await apiGet<any>('/tournaments', { limit: 1000 })
      const list = Array.isArray(data?.tournaments) ? data.tournaments : (Array.isArray(data) ? data : [])
      const mapped = list.map((t: any) => ({
        id: t.id,
        name: t.name,
        status: t.status,
        startDate: t.startDate,
        endDate: t.endDate
      }))
      setTournaments(mapped)
      
      // Проверяем, есть ли сохраненный турнир в списке
      const savedTournamentId = localStorage.getItem('reports-player-settlements-tournament-id')
      if (savedTournamentId !== null) {
        // Если сохранено пустое значение ("Все") или турнир существует в списке
        if (savedTournamentId === '' || mapped.find(t => t.id === savedTournamentId)) {
          setTournamentId(savedTournamentId)
        } else if (mapped.length > 0) {
          // если сохраненный турнир не найден, попробуем выбрать первый ACTIVE
          const active = mapped.find(t => t.status === 'ACTIVE') || mapped[0]
          setTournamentId(active.id)
        }
      } else if (!tournamentId && mapped.length > 0) {
        // если ничего не выбрано и нет сохраненного, попробуем выбрать первый ACTIVE
        const active = mapped.find(t => t.status === 'ACTIVE') || mapped[0]
        setTournamentId(active.id)
      }
    } catch {}
  }

  const fetchData = async () => {
    setLoading(true)
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      if (tournamentId) params.tournamentId = tournamentId
      if (groupByTournament) params.groupByTournament = 'true'
      const data = await apiGet<any>('/reports/player-settlements-movements', params)
      const arr = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
      const list: Row[] = arr.map((r: any) => ({
        playerId: r.playerId || r.id,
        playerName: r.playerName || r.name,
        opening: Number(r.opening || 0),
        incoming: Number(r.incoming || 0),
        outgoing: Number(r.outgoing || 0),
        closing: Number(r.closing || 0),
        tournaments: Array.isArray(r.tournaments)
          ? r.tournaments.map((t: any) => ({
              tournamentId: t.tournamentId ?? null,
              tournamentName: String(t.tournamentName || 'Без турнира'),
              tournamentEndDate: t.tournamentEndDate || null,
              opening: Number(t.opening || 0),
              incoming: Number(t.incoming || 0),
              outgoing: Number(t.outgoing || 0),
              closing: Number(t.closing || 0)
            }))
          : undefined
      }))
      // фильтр: показывать только с движением/остатками
      const filtered = list.filter(r => Math.abs(r.opening) + Math.abs(r.incoming) + Math.abs(r.outgoing) + Math.abs(r.closing) !== 0)
      // сортировка по имени игрока в алфавитном порядке (ru)
      filtered.sort((a,b) => String(a.playerName||'').localeCompare(String(b.playerName||''), 'ru'))
      setRows(filtered)
    } catch {
      setRows([])
    } finally {
      setLoading(false)
    }
  }

  const fetchDetails = async (
    playerId: string,
    playerName: string,
    opening: number,
    tournamentIdOverride?: string | null,
    tournamentNameOverride?: string
  ) => {
    setDLoading(true)
    setDetailFor({
      id: playerId,
      name: playerName,
      opening,
      tournamentName: tournamentNameOverride
    })
    try {
      const params: Record<string, string> = {}
      if (from) params.from = from
      if (to) params.to = to
      const effectiveTournamentId =
        tournamentIdOverride !== undefined
          ? (tournamentIdOverride || '')
          : tournamentId
      if (effectiveTournamentId) params.tournamentId = effectiveTournamentId
      const data = await apiGet<any>(`/reports/player-settlements-movements/${playerId}/details`, params)
      const arr = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : [])
      const list: Detail[] = arr.map((d: any) => ({
        id: d.id,
        date: d.date,
        document: d.document,
        type: d.type,
        amount: Number(d.amount || 0),
        tournament: d.tournament,
        level: d.level,
      }))
      setDetails(list)
    } catch {
      setDetails([])
    } finally {
      setDLoading(false)
    }
  }

  const detailRowsWithBalances = useMemo(() => {
    let running = Number(detailFor?.opening || 0)
    return details.map((d) => {
      const incoming = d.type === 'RECEIVABLE' ? Number(d.amount || 0) : 0
      const outgoing = d.type === 'PAYMENT' || d.type === 'ADJUSTMENT' ? Number(d.amount || 0) : 0
      const opening = running
      const closing = opening + incoming - outgoing
      running = closing
      return {
        ...d,
        opening,
        incoming,
        outgoing,
        closing
      }
    })
  }, [details, detailFor?.opening])

  const detailTotals = useMemo(() => {
    const opening = detailRowsWithBalances.length > 0
      ? Number(detailRowsWithBalances[0].opening || 0)
      : Number(detailFor?.opening || 0)
    const incoming = detailRowsWithBalances.reduce((sum, row) => sum + Number(row.incoming || 0), 0)
    const outgoing = detailRowsWithBalances.reduce((sum, row) => sum + Number(row.outgoing || 0), 0)
    const closing = detailRowsWithBalances.length > 0
      ? Number(detailRowsWithBalances[detailRowsWithBalances.length - 1].closing || 0)
      : opening
    return { opening, incoming, outgoing, closing }
  }, [detailRowsWithBalances, detailFor?.opening])

  useEffect(() => {
    fetchTournaments()
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupByTournament])

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold text-gray-900">Взаиморасчеты с игроками</h2>
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs text-gray-600 mb-1">С даты</label>
          <input type="date" className="border rounded px-3 py-2" value={from} onChange={e=>handleFromChange(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">По дату</label>
          <input type="date" className="border rounded px-3 py-2" value={to} onChange={e=>handleToChange(e.target.value)} />
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Турнир</label>
          <select className="border rounded px-3 py-2 min-w-[220px]" value={tournamentId} onChange={e=>handleTournamentChange(e.target.value)}>
            <option value="">Все</option>
            {tournamentsGrouped.map(group => (
              <optgroup key={group.label} label={group.label}>
                {group.items.map(t => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </optgroup>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs text-gray-600 mb-1">Игрок</label>
          <input
            type="text"
            className="border rounded px-3 py-2 min-w-[220px]"
            value={playerFilter}
            onChange={e => setPlayerFilter(e.target.value)}
            placeholder="Поиск по имени"
          />
        </div>
        <label className="flex items-center gap-2 pb-2">
          <input
            type="checkbox"
            className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
            checked={groupByTournament}
            onChange={(e) => setGroupByTournament(e.target.checked)}
          />
          <span className="text-sm text-gray-700">По турнирам</span>
        </label>
        <button className="px-4 py-2 border rounded bg-white hover:bg-gray-50" onClick={fetchData}>
          Обновить
        </button>
        <button 
          className="px-4 py-2 border rounded bg-gray-100 hover:bg-gray-200 text-gray-700" 
          onClick={() => {
            setTournamentId('')
            setFrom('')
            setTo('')
            setPlayerFilter('')
            setGroupByTournament(false)
            localStorage.setItem('reports-player-settlements-tournament-id', '')
            localStorage.removeItem('reports-player-settlements-from')
            localStorage.removeItem('reports-player-settlements-to')
            fetchData()
          }}
        >
          Сбросить
        </button>
      </div>

      <div className="overflow-auto">
        <table className="min-w-full bg-white border rounded">
          <thead>
            <tr className="bg-gray-50 text-left text-sm text-gray-600">
              <th className="px-3 py-2 border-b text-left">Игрок</th>
              <th className="px-3 py-2 border-b text-right">Нач.ост.</th>
              <th className="px-3 py-2 border-b text-right">Начислено</th>
              <th className="px-3 py-2 border-b text-right">Оплачено</th>
              <th className="px-3 py-2 border-b text-right">Кон.ост.</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {loading && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Загрузка...</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Нет данных</td></tr>
            )}
            {!loading && rows.length > 0 && filteredRows.length === 0 && (
              <tr><td className="px-3 py-3 text-gray-500" colSpan={5}>Нет данных по фильтру игрока</td></tr>
            )}
            {!loading && filteredRows.map(r => (
              <React.Fragment key={r.playerId}>
                <tr className="hover:bg-gray-50 cursor-pointer" onClick={()=>fetchDetails(r.playerId, r.playerName, r.opening)}>
                  <td className="px-3 py-2 border-b text-left font-medium">{r.playerName}</td>
                  <td className="px-3 py-2 border-b text-right">{formatNumber(r.opening)}</td>
                  <td className="px-3 py-2 border-b text-right">{formatNumber(r.incoming)}</td>
                  <td className="px-3 py-2 border-b text-right">{formatNumber(r.outgoing)}</td>
                  <td className="px-3 py-2 border-b text-right font-medium">{formatNumber(r.closing)}</td>
                </tr>
                {groupByTournament && (r.tournaments || []).map((t, idx) => (
                  <tr
                    key={`${r.playerId}-tournament-${t.tournamentId || 'none'}-${idx}`}
                    className="bg-gray-50/40 hover:bg-gray-100 cursor-pointer"
                    onClick={() => fetchDetails(r.playerId, r.playerName, t.opening, t.tournamentId, t.tournamentName)}
                  >
                    <td className="px-3 py-2 border-b text-left text-gray-700">↳ {t.tournamentName}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(t.opening)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(t.incoming)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(t.outgoing)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(t.closing)}</td>
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
          {!loading && filteredRows.length > 0 && (
            <tfoot>
              <tr className="bg-gray-50 text-base font-bold">
                <td className="px-3 py-2 border-t text-left font-bold">Итого</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.opening)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.incoming)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.outgoing)}</td>
                <td className="px-3 py-2 border-t text-right">{formatNumber(total.closing)}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Детализация */}
      {detailFor && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
          <div className="bg-white w-full max-w-[83rem] rounded shadow p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="font-semibold text-gray-900">
                Детализация: {detailFor.name}
                {detailFor.tournamentName ? ` • ${detailFor.tournamentName}` : ''}
              </div>
              <button className="px-3 py-1 border rounded hover:bg-gray-50" onClick={()=>{ setDetailFor(null); setDetails([]) }}>Закрыть</button>
            </div>

            <div className="overflow-auto max-h-[70vh]">
              <table className="min-w-full bg-white border rounded">
                <thead>
                  <tr className="bg-gray-50 text-left text-sm text-gray-600">
                    <th className="px-3 py-2 border-b">Дата</th>
                    <th className="px-3 py-2 border-b">Документ</th>
                    <th className="px-3 py-2 border-b">Уровень</th>
                    <th className="px-3 py-2 border-b">Тип</th>
                    <th className="px-3 py-2 border-b text-right">Нач.ост.</th>
                    <th className="px-3 py-2 border-b text-right">Начислено</th>
                    <th className="px-3 py-2 border-b text-right">Оплачено</th>
                    <th className="px-3 py-2 border-b text-right">Кон.ост.</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-3 py-2 border-b">Итого</td>
                    <td className="px-3 py-2 border-b" />
                    <td className="px-3 py-2 border-b" />
                    <td className="px-3 py-2 border-b" />
                    <td className="px-3 py-2 border-b text-right">{formatNumber(detailTotals.opening)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(detailTotals.incoming)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(detailTotals.outgoing)}</td>
                    <td className="px-3 py-2 border-b text-right">{formatNumber(detailTotals.closing)}</td>
                  </tr>
                  {dLoading && (
                    <tr><td className="px-3 py-3 text-gray-500" colSpan={8}>Загрузка...</td></tr>
                  )}
                  {!dLoading && details.length === 0 && (
                    <tr><td className="px-3 py-3 text-gray-500" colSpan={8}>Нет данных</td></tr>
                  )}
                  {!dLoading && detailRowsWithBalances.map((d, idx) => (
                    <tr key={idx}>
                      <td className="px-3 py-2 border-b whitespace-nowrap">{d.date?.slice(0,10)}</td>
                      <td className="px-3 py-2 border-b">
                        {d.document ? (
                          <button className="text-blue-600 hover:underline" onClick={()=>{ setViewData({ ...d, playerName: detailFor?.name }); setViewOpen(true); }}>
                            {d.document}
                          </button>
                        ) : '-'}
                      </td>
                      <td className="px-3 py-2 border-b">{d.level || '-'}</td>
                      <td className="px-3 py-2 border-b">{d.type}</td>
                      <td className="px-3 py-2 border-b text-right">{formatNumber(d.opening)}</td>
                      <td className="px-3 py-2 border-b text-right">{formatNumber(d.incoming)}</td>
                      <td className="px-3 py-2 border-b text-right">{formatNumber(d.outgoing)}</td>
                      <td className="px-3 py-2 border-b text-right">{formatNumber(d.closing)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <PlayerSettlementViewModal open={viewOpen} onClose={()=>setViewOpen(false)} data={viewData} />
    </div>
  )
}

export default ReportsPlayerSettlements


